import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Save, Video } from "lucide-react";
import { Money, StatusBadge } from "@/components/shared/Common";

export default function DoctorConsultation() {
  const { apptId } = useParams();
  const [appt, setAppt] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [notes, setNotes] = useState({ summary:"", clinical_notes:"", advice:"", follow_ups:"", tests:"", referrals:"", medications:"", next_appointment:"" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/appointments/${apptId}`).then(r => setAppt(r.data));
    api.get(`/appointments/${apptId}/notes`).then(r => { if (r.data) setNotes(n => ({ ...n, ...r.data })); }).catch(()=>{});
  }, [apptId]);

  const loadBrief = async () => {
    setBriefLoading(true);
    try {
      const { data } = await api.get(`/appointments/${apptId}/brief`);
      setBrief(data.brief);
    } catch { toast.error("Brief unavailable"); }
    finally { setBriefLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/appointments/${apptId}/notes`, { appointment_id: apptId, ...notes });
      toast.success("Notes saved · consultation completed");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  if (!appt) return <div className="text-center text-[#475569]">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="cm-eyebrow mb-2">Consultation</div>
          <h1 className="cm-h2">{(appt.slot_datetime||"").replace("T"," ").slice(0,16)}</h1>
          <div className="text-sm text-[#475569] capitalize">{appt.consultation_type} · <Money amount={appt.doctor_payout} currency={appt.currency}/></div>
        </div>
        <StatusBadge status={appt.status}/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="cm-card">
            <div className="flex items-center justify-between mb-3">
              <div className="cm-eyebrow"><Sparkles size={12} className="inline mr-1"/> AI-assisted brief</div>
              <button className="cm-btn-outline text-xs py-1.5 px-3" onClick={loadBrief} disabled={briefLoading} data-testid="load-brief">{briefLoading ? "Generating…" : brief ? "Regenerate" : "Generate"}</button>
            </div>
            {brief ? (
              <div className="text-sm text-[#475569] leading-relaxed whitespace-pre-wrap" data-testid="brief-content">{brief}</div>
            ) : (
              <div className="text-sm text-[#475569]">Click generate to prepare the AI-assisted patient summary.</div>
            )}
            <div className="mt-3 text-[10px] uppercase tracking-wider text-[#94a3b8]">AI-assisted · verify with patient</div>
          </div>

          <div className="cm-card">
            <div className="cm-eyebrow mb-2">Patient input</div>
            <div className="text-sm space-y-1">
              <div><b>Reason:</b> {appt.reason || "—"}</div>
              <div><b>Duration:</b> {appt.duration || "—"}</div>
              <div><b>Severity:</b> {appt.severity || "—"}</div>
              <div><b>Symptoms:</b> {appt.symptoms || "—"}</div>
              <div><b>Concerns:</b> {appt.patient_concerns || "—"}</div>
            </div>
          </div>

          <Link to={`/doctor/video/${apptId}`} className="cm-btn-primary w-full" data-testid="dr-join-call"><Video size={14}/> Start video consult</Link>
        </div>

        <div className="lg:col-span-2 cm-card">
          <h3 className="font-semibold text-[#1a2b4a] mb-4">Consultation notes</h3>
          <div className="space-y-3">
            <Field label="Summary" value={notes.summary} onChange={v=>setNotes({...notes, summary:v})} testid="note-summary" required/>
            <Field label="Clinical notes" value={notes.clinical_notes} onChange={v=>setNotes({...notes, clinical_notes:v})} testid="note-clinical" multi/>
            <Field label="Advice" value={notes.advice} onChange={v=>setNotes({...notes, advice:v})} testid="note-advice"/>
            <Field label="Medications" value={notes.medications} onChange={v=>setNotes({...notes, medications:v})} testid="note-medications"/>
            <Field label="Follow-ups" value={notes.follow_ups} onChange={v=>setNotes({...notes, follow_ups:v})} testid="note-followups"/>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Tests requested" value={notes.tests} onChange={v=>setNotes({...notes, tests:v})} testid="note-tests"/>
              <Field label="Referrals" value={notes.referrals} onChange={v=>setNotes({...notes, referrals:v})} testid="note-referrals"/>
            </div>
            <Field label="Next appointment" value={notes.next_appointment} onChange={v=>setNotes({...notes, next_appointment:v})} testid="note-next"/>
            <button className="cm-btn-primary" onClick={save} disabled={saving || !notes.summary} data-testid="note-save"><Save size={14}/> {saving?"Saving…":"Save and complete"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testid, multi }) => (
  <div>
    <label className="cm-label">{label}</label>
    {multi ? <textarea className="cm-input min-h-[90px]" value={value} onChange={e=>onChange(e.target.value)} data-testid={testid}/> : <input className="cm-input" value={value} onChange={e=>onChange(e.target.value)} data-testid={testid}/>}
  </div>
);
